import {
  clearCachedCurrentToken,
  getCachedCurrentToken,
  setCachedCurrentToken,
} from './cache';
import { fetchToken, Token } from './fetchToken';
import { getLoginUrl } from '../helpers/config';

type HTTPErrorWithAuthenticationUrl = {
  authenticationUrl: string;
};

let latestTokenPromise: Promise<Token> | null = null;

export const clearToken = () => {
  clearCachedCurrentToken();
};

export const getAuthenticationUrl = (res: HTTPErrorWithAuthenticationUrl) =>
  res.authenticationUrl;

// IMPORTANT! change the version here anytime `innerGetCurrentTokenWithoutRedirect` is being updated!
const getTokenFunctionVersion = 1;

const innerGetCurrentTokenWithoutRedirect = () => {
  if (latestTokenPromise) {
    return latestTokenPromise;
  }
  const currentToken = getCachedCurrentToken();
  if (
    !currentToken ||
    // The token refresh is allowed 3 minutes prior to expiry
    new Date(currentToken.expiresAt).getTime() - Date.now() <= 180 * 1000
  ) {
    latestTokenPromise = fetchToken()
      .then((token) => {
        latestTokenPromise = null;
        setCachedCurrentToken(token);
        return token;
      })
      .catch((e) => {
        latestTokenPromise = null;
        if (getAuthenticationUrl(e)) {
          clearToken();
        }
        throw e;
      });
    return latestTokenPromise;
  }
  return Promise.resolve(currentToken);
};

const globalGetTokenFunctionKeyName = '__ACSGetAccessToken';
const globalGetTokenFunctionVersionKeyName = '__ACSGetAccessTokenVersion';

declare global {
  interface Window {
    [globalGetTokenFunctionKeyName]?: () => Promise<Token>;
    [globalGetTokenFunctionVersionKeyName]?: number;
  }
}

export const getCurrentTokenWithoutRedirect = (): Promise<Token> => {
  let fn = window[globalGetTokenFunctionKeyName];
  const fnVersion = window[globalGetTokenFunctionVersionKeyName];
  if (!fn || !fnVersion || fnVersion < getTokenFunctionVersion) {
    fn = window[globalGetTokenFunctionKeyName] =
      innerGetCurrentTokenWithoutRedirect;
    window[globalGetTokenFunctionVersionKeyName] = getTokenFunctionVersion;
  }
  return fn();
};

export const getCurrentToken = () =>
  getCurrentTokenWithoutRedirect().catch((e) => {
    const authenticationUrl = getAuthenticationUrl(e);
    if (authenticationUrl) {
      window.location.assign(authenticationUrl);
    }
    throw e;
  });

/**
 * Ends the session and redirects to the sign-out endpoint.
 *
 * The redirect discards the JS context, so any other teardown a caller needs must
 * already have happened before this is called.
 *
 * @param redirectUrl Where to return after the sign-out round trip.
 * @returns Nothing.
 */
export const logout = (redirectUrl: string = window.location.href) => {
  clearToken();
  window.location.assign(
    `${getLoginUrl()}/api/v1/authentication/logout?redirectUrl=${redirectUrl}`,
  );
};

export type LoginRedirectOptions = {
  /** Where to return after authentication. Defaults to the current URL. */
  redirectUrl?: string;
  /** Force interactive re-auth (prompt=login) even if a valid session exists. */
  forceLogin?: boolean;
};

/**
 * Builds the HQ `/login` URL that starts the Oxygen authorize flow. With
 * `forceLogin`, appends `prompt=login` so the user must re-authenticate even
 * when they already have a session. Navigate to it with `window.location.assign`.
 *
 * Requires a browser context: it reads `window.location` for the default
 * `redirectUrl` and derives the host from `window` (same caveat as
 * `getLoginUrl()` / `logout`), so it is not usable server-side.
 */
export const getLoginRedirectUrl = ({
  redirectUrl = window.location.href,
  forceLogin = false,
}: LoginRedirectOptions = {}): string => {
  const params = new URLSearchParams({ redirectUrl });
  if (forceLogin) {
    params.set('prompt', 'login');
  }
  return `${getLoginUrl()}/api/v1/authentication/login?${params.toString()}`;
};

export const convertToDocsToken = ({
  accessToken,
  expiresAt,
}: {
  accessToken: string;
  expiresAt: string;
}) => ({
  token: accessToken,
  token_expiration: Math.round(
    (new Date(expiresAt).getTime() - Date.now()) / 1000,
  ),
});
